/**
 * Global Shortcuts Service
 * 
 * Manages system-wide keyboard shortcuts that work anywhere in RiverOS.
 * Similar to Cmd+K for AI, but for any global action.
 * 
 * Architecture:
 * - Registers at App.tsx level (below WindowWrapper level)
 * - Single event listener for all global shortcuts
 * - Apps can register their global shortcuts here
 * - Handles conflicts and priority
 * - Works even when app isn't open
 */

import { createFileLogger } from '../utils/logger';

const log = createFileLogger('GlobalShortcuts', { enabled: false });

export interface GlobalShortcut {
  id: string;
  key: string;
  modifiers: {
    cmd?: boolean;
    ctrl?: boolean;
    shift?: boolean;
    alt?: boolean;
  };
  description: string;
  handler: () => void | Promise<void>;
  priority?: number; // Higher priority = executes first (for conflicts)
  enabled?: boolean;
}

class GlobalShortcutsService {
  private shortcuts: Map<string, GlobalShortcut> = new Map();
  private isListening = false;

  /**
   * Register a global shortcut
   */
  register(shortcut: GlobalShortcut) {
    const key = this.getShortcutKey(shortcut.key, shortcut.modifiers);
    
    log.log('🎹 [GlobalShortcuts] Registering shortcut:', {
      id: shortcut.id,
      key: key,
      description: shortcut.description,
      modifiers: shortcut.modifiers
    });
    
    if (this.shortcuts.has(key)) {
      console.warn('⚠️ [GlobalShortcuts] Shortcut already registered, overwriting:', {
        oldId: this.shortcuts.get(key)?.id,
        newId: shortcut.id,
        key: key
      });
    }

    this.shortcuts.set(key, shortcut);
    log.log('✅ [GlobalShortcuts] Registered global shortcut:', key, {
      id: shortcut.id,
      totalShortcuts: this.shortcuts.size
    });

    // Start listening if not already
    if (!this.isListening) {
      log.log('🎧 [GlobalShortcuts] Starting listener...');
      this.startListening();
    }
  }

  /**
   * Unregister a global shortcut
   */
  unregister(shortcutId: string) {
    log.log('🗑️ [GlobalShortcuts] Unregistering shortcut:', shortcutId);
    
    for (const [key, shortcut] of this.shortcuts.entries()) {
      if (shortcut.id === shortcutId) {
        this.shortcuts.delete(key);
        log.log('✅ [GlobalShortcuts] Unregistered:', key, {
          id: shortcutId,
          remainingShortcuts: this.shortcuts.size
        });
        break;
      }
    }

    // Stop listening if no shortcuts left
    if (this.shortcuts.size === 0) {
      log.log('🛑 [GlobalShortcuts] No shortcuts left, stopping listener');
      this.stopListening();
    }
  }

  /**
   * Enable/disable a shortcut without unregistering
   */
  setEnabled(shortcutId: string, enabled: boolean) {
    for (const [key, shortcut] of this.shortcuts.entries()) {
      if (shortcut.id === shortcutId) {
        shortcut.enabled = enabled;
        log.info(`${enabled ? 'Enabled' : 'Disabled'} global shortcut: ${key}`);
        break;
      }
    }
  }

  /**
   * Get all registered shortcuts (for UI display)
   */
  getAllShortcuts(): GlobalShortcut[] {
    return Array.from(this.shortcuts.values());
  }

  /**
   * Generate shortcut key for map
   */
  private getShortcutKey(key: string, modifiers: GlobalShortcut['modifiers']): string {
    if (!key) {
      console.warn('⚠️ [GlobalShortcuts] Invalid key provided to getShortcutKey');
      return '';
    }
    
    const parts: string[] = [];
    if (modifiers.cmd) parts.push('cmd');
    if (modifiers.ctrl) parts.push('ctrl');
    if (modifiers.shift) parts.push('shift');
    if (modifiers.alt) parts.push('alt');
    parts.push(key.toLowerCase());
    return parts.join('+');
  }

  /**
   * Check if user is typing in an input field
   */
  private isTypingInInput(target: HTMLElement): boolean {
    return (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable ||
      target.closest('[contenteditable="true"]') !== null
    );
  }

  /**
   * Handle keyboard event
   */
  private handleKeyDown = (e: KeyboardEvent) => {
    const target = e.target as HTMLElement;

    // Guard against undefined keys (can happen with IME composition events)
    if (!e.key) {
      return;
    }

    // Build current key combination
    // Normalize modifiers: treat Ctrl as Cmd for cross-platform compatibility
    // On Mac: metaKey = Cmd, ctrlKey = Ctrl
    // On Windows/Linux: metaKey = Windows key, ctrlKey = Ctrl (which we treat as Cmd)
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const cmdPressed = e.metaKey || (!isMac && e.ctrlKey);
    
    const modifiers: GlobalShortcut['modifiers'] = {
      cmd: cmdPressed,
      ctrl: isMac && e.ctrlKey, // Only treat Ctrl separately on Mac
      shift: e.shiftKey,
      alt: e.altKey
    };

    const key = this.getShortcutKey(e.key, modifiers);
    
    const shortcut = this.shortcuts.get(key);

    if (!shortcut) {
      // Shortcut not registered, ignore silently
      return;
    }
    
    if (shortcut.enabled === false) {
      log.log('⏸️ [GlobalShortcuts] Shortcut disabled:', key);
      return;
    }

    // Global shortcuts should work EVERYWHERE (even in inputs)
    // This is intentional - they're truly global
    // If you want input-aware behavior, use app-level shortcuts instead

    log.log('🎯 [GlobalShortcuts] Shortcut matched! Executing:', {
      key: key,
      id: shortcut.id,
      description: shortcut.description
    });

    // Prevent default browser behavior
    e.preventDefault();
    e.stopPropagation();

    // Execute handler
    try {
      shortcut.handler();
      log.log('✅ [GlobalShortcuts] Handler executed successfully');
    } catch (error) {
      console.error('❌ [GlobalShortcuts] Error executing handler:', error);
      log.error(`Error executing global shortcut ${key}:`, error);
    }
  };

  /**
   * Start listening for keyboard events
   */
  private startListening() {
    if (this.isListening) {
      log.log('⚠️ [GlobalShortcuts] Already listening, skipping');
      return;
    }

    log.log('🎧 [GlobalShortcuts] Adding keydown event listener (capture phase)');
    document.addEventListener('keydown', this.handleKeyDown, { capture: true });
    this.isListening = true;
    log.log('✅ [GlobalShortcuts] Now listening for shortcuts:', {
      isListening: this.isListening,
      shortcutsCount: this.shortcuts.size,
      shortcuts: Array.from(this.shortcuts.keys())
    });
  }

  /**
   * Stop listening for keyboard events
   */
  private stopListening() {
    if (!this.isListening) {
      log.log('⚠️ [GlobalShortcuts] Not listening, skipping stop');
      return;
    }

    log.log('🛑 [GlobalShortcuts] Removing keydown event listener');
    document.removeEventListener('keydown', this.handleKeyDown, { capture: true });
    this.isListening = false;
    log.log('✅ [GlobalShortcuts] Stopped listening');
  }

  /**
   * Cleanup (call on app unmount)
   */
  cleanup() {
    this.stopListening();
    this.shortcuts.clear();
    log.info('Global shortcuts service cleaned up');
  }
}

// Singleton instance
export const globalShortcutsService = new GlobalShortcutsService();

/**
 * React hook for registering global shortcuts
 * Auto-cleanup on unmount
 */
export const useGlobalShortcut = (shortcut: GlobalShortcut) => {
  React.useEffect(() => {
    globalShortcutsService.register(shortcut);
    
    return () => {
      globalShortcutsService.unregister(shortcut.id);
    };
  }, [shortcut.id]); // Only re-register if ID changes
};

// Export for direct import
import React from 'react';

